import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CrownSimpleIcon, ArrowRightIcon, TrophyIcon, FireIcon, TargetIcon, StarIcon } from '@phosphor-icons/react';
import { ThemeContext } from '../../../../apps/frontend/src/context/ThemeStore';
import { AppLayout } from '../Layout/AppLayout';
import { usePackStorage } from '../Layout/usePackStorage';
import JeeMainPaywallSheet from './JeeMainPaywallSheet';
import { hasActiveJeeMainPack } from './jeeMainPaywall';
import QuizrrLogoLight from '../../public/Images/QuizrrLogoLight.svg';
import QuizrrLogoDark from '../../public/Images/QuizrrLogoDark.svg';
import './Challenge99Landing.css';

// Large decorative trophy illustration for the closing CTA banner.
const TrophyIllustration = () => (
  <svg viewBox="0 0 160 196" aria-hidden="true">
    <defs>
      <linearGradient id="c99jb" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#8a4b0b" />
        <stop offset=".18" stopColor="#e8a317" />
        <stop offset=".38" stopColor="#fff0a8" />
        <stop offset=".55" stopColor="#f6b72a" />
        <stop offset=".8" stopColor="#c77a0d" />
        <stop offset="1" stopColor="#7a3f08" />
      </linearGradient>
      <linearGradient id="c99jh" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#fff6c2" />
        <stop offset=".5" stopColor="#f4b324" />
        <stop offset="1" stopColor="#9a5208" />
      </linearGradient>
      <linearGradient id="c99jp" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#45454c" />
        <stop offset="1" stopColor="#18181b" />
      </linearGradient>
      <linearGradient id="c99js" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#b8710f" />
        <stop offset=".4" stopColor="#ffe89a" />
        <stop offset="1" stopColor="#a65d0a" />
      </linearGradient>
      <clipPath id="c99jc">
        <path d="M36 24H124V58C124 94 104 118 80 120C56 118 36 94 36 58Z" />
      </clipPath>
    </defs>
    <ellipse cx="80" cy="188" rx="52" ry="6" fill="#000" opacity=".35" />
    <rect x="34" y="162" width="92" height="20" rx="6" fill="url(#c99jp)" />
    <rect x="34" y="162" width="92" height="3" rx="1.5" fill="#fff" opacity=".18" />
    <rect x="46" y="150" width="68" height="14" rx="4" fill="url(#c99js)" />
    <path d="M66 122C70 134 69 142 62 152H98C91 142 90 134 94 122Z" fill="url(#c99jb)" />
    <ellipse cx="80" cy="132" rx="15" ry="6.5" fill="url(#c99js)" />
    <path d="M36 36C8 32 6 70 42 86" fill="none" stroke="url(#c99jh)" strokeWidth="9" strokeLinecap="round" />
    <path d="M124 36C152 32 154 70 118 86" fill="none" stroke="url(#c99jh)" strokeWidth="9" strokeLinecap="round" />
    <path d="M36 24H124V58C124 94 104 118 80 120C56 118 36 94 36 58Z" fill="url(#c99jb)" />
    <path d="M45 32C42 62 52 90 68 106" fill="none" stroke="#fff" strokeWidth="5" strokeLinecap="round" opacity=".38" />
    <rect x="-70" y="10" width="34" height="120" fill="#fff" opacity=".55" clipPath="url(#c99jc)" />
    <ellipse cx="80" cy="24" rx="44" ry="8.5" fill="url(#c99js)" />
    <ellipse cx="80" cy="24.5" rx="38" ry="5.5" fill="#4a1d06" opacity=".6" />
    <path
      d="M80 46l7.6 15.4 17 2.5-12.3 12 2.9 16.9L80 84.7 64.8 92.8l2.9-16.9-12.3-12 17-2.5z"
      fill="#fff8d6"
      opacity=".92"
    />
  </svg>
);

const WhyTrophyIcon = (props) => <TrophyIcon weight="regular" {...props} />;
const WhyFlameIcon = (props) => <FireIcon weight="regular" {...props} />;
const WhyTargetIcon = (props) => <TargetIcon weight="regular" {...props} />;

const WHY_ROWS = [
  {
    flip: false,
    icon: WhyTrophyIcon,
    lineIcon: 'trophy',
    title: 'Compete',
    sub: '1 in every 5 of India’s 99 percentilers is from Quizrr.',
    accent: 'Now see where you stand.',
    items: [
      'Complete daily tasks and earn points',
      'Get your All India Rank among everyone in the challenge',
      'Leaderboard resets every week',
    ],
  },
  {
    flip: true,
    icon: WhyFlameIcon,
    lineIcon: 'flame',
    title: 'Stay Consistent',
    sub: 'JEE is not cracked in one great day.',
    accent: 'It’s cracked by showing up every day.',
    items: [
      'Stay on track for 99 straight days',
      'Revise chapters through quick 10–20 question mocks',
      'Build a streak that makes skipping a day harder',
    ],
  },
  {
    flip: false,
    icon: WhyTargetIcon,
    lineIcon: 'target',
    title: 'Prepare Smarter',
    sub: 'Stop collecting resources.',
    accent: 'Start solving what actually matters for JEE.',
    items: [
      'Practice questions selected for JEE relevance',
      'Revise the chapters that can actually move your score',
      'Less random solving. More focused preparation.',
    ],
  },
];

const LINE_ICONS = {
  trophy: (
    <svg className="c99-li" viewBox="0 0 24 24">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6M18 9h1.5a2.5 2.5 0 0 0 0-5H18M4 22h16M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
      <path className="f" d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
    </svg>
  ),
  flame: (
    <svg className="c99-li" viewBox="0 0 24 24">
      <path
        className="f"
        d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"
      />
    </svg>
  ),
  target: (
    <svg className="c99-li" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" />
      <circle className="f" cx="12" cy="12" r="6" />
      <circle className="w" cx="12" cy="12" r="2.2" />
    </svg>
  ),
};

const SOCIAL_PROOF_POLL_MS = 4000; // the backend re-buckets the rotating entry every 10s, but we
// still poll faster so the joined counter feels live - a no-op fetch is cheap.

function WhyRow({ row, index }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const Icon = row.icon;

  return (
    <div ref={ref} className={`c99-why-r${row.flip ? ' flip' : ''}${visible ? ' vis' : ''}`}>
      <div className="c99-why-v" aria-hidden="true">
        <div className="c99-why-i">{LINE_ICONS[row.lineIcon]}</div>
      </div>
      <div className="c99-why-x">
        <h3 className="c99-why-t">
          <Icon className="c99-ic" />
          {row.title}
        </h3>
        <p className="c99-why-s">
          {row.sub}
          <em>{row.accent}</em>
        </p>
        <ul className="c99-why-l">
          {row.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function Challenge99Landing() {
  const navigate = useNavigate();
  const { isDarkTheme } = useContext(ThemeContext);
  const { purchasedPacks, selectedPack, isLoading: packsLoading } = usePackStorage();
  const [checkingEnrollment, setCheckingEnrollment] = useState(true);
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState(null);

  const [joinedCount, setJoinedCount] = useState(null);
  const [bump, setBump] = useState(false);
  const [tickerEntry, setTickerEntry] = useState(null);
  const [tickerKey, setTickerKey] = useState(0);

  // Already-joined students skip the landing page entirely.
  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/challenge/home')
      .then(() => {
        if (!cancelled) navigate('/challenge/home', { replace: true });
      })
      .catch(() => {
        if (!cancelled) setCheckingEnrollment(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    if (checkingEnrollment) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const res = await axios.get('/api/challenge/social-proof');
        if (cancelled) return;
        const { joinedCount: nextCount, rotatingEntry } = res?.data?.data || {};

        if (typeof nextCount === 'number') {
          setJoinedCount((prev) => {
            if (prev !== null && prev !== nextCount) {
              setBump(true);
              setTimeout(() => setBump(false), 500);
            }
            return nextCount;
          });
        }

        if (rotatingEntry) {
          setTickerEntry((prev) => {
            const changed = !prev || prev.name !== rotatingEntry.name || prev.city !== rotatingEntry.city;
            if (changed) setTickerKey((k) => k + 1);
            return rotatingEntry;
          });
        }
      } catch {
        // Social proof is cosmetic - fail silently and retry on the next poll.
      }
    };

    poll();
    const interval = setInterval(poll, SOCIAL_PROOF_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [checkingEnrollment]);

  const handleJoin = async () => {
    setJoining(true);
    setJoinError(null);
    try {
      await axios.post('/api/challenge/join');
      navigate('/challenge/home', { state: { justJoined: true } });
    } catch (err) {
      // Surfaces the backend's own message when it has one (e.g. joinChallenge()'s "An active JEE
      // Main pack is required..." 403 - a defense-in-depth check behind our own paywall gate here,
      // so this only ever fires on a bypassed/edge-case call) - same err.response?.data?.message
      // convention used elsewhere in the app, rather than always showing a generic fallback.
      setJoinError(err.response?.data?.message || 'Something went wrong. Please try again.');
      setJoining(false);
    }
  };

  // The AppLayout shell (sidenav included) renders immediately regardless of loading state, so
  // navigating here never blanks the whole screen - only the content slot shows a placeholder
  // while the enrollment/pack checks are in flight.
  const stillChecking = checkingEnrollment || packsLoading;
  // The challenge is gated behind an active JEE Main test series purchase (a product decision,
  // not a backend rule - the challenge API itself is free/open). Direct-URL visits land here too,
  // not just the sidenav's Challenge tab, so this check has to live on the page itself as well.
  const needsPaywall = !stillChecking && !hasActiveJeeMainPack(purchasedPacks);

  return (
    <AppLayout purchasedPacks={purchasedPacks} selectedPack={selectedPack}>
      {stillChecking ? (
        <div className="c99-loading" aria-hidden="true" />
      ) : needsPaywall ? (
        <JeeMainPaywallSheet open onClose={() => navigate('/v2/home')} />
      ) : (
        <div className="c99-landing">
          <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
            <defs>
              <linearGradient id="c99GStroke" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#f1c76a" />
                <stop offset=".4" stopColor="#f1c76a" />
                <stop offset=".5" stopColor="#fff7de" />
                <stop offset=".6" stopColor="#f1c76a" />
                <stop offset="1" stopColor="#f1c76a" />
                <animateTransform
                  attributeName="gradientTransform"
                  type="translate"
                  values="-1 -1;-1 -1;1 1"
                  keyTimes="0;.45;1"
                  dur="5s"
                  repeatCount="indefinite"
                />
              </linearGradient>
              <linearGradient id="c99GFill" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#fbe3a3" stopOpacity=".32" />
                <stop offset="1" stopColor="#e9b54e" stopOpacity=".18" />
              </linearGradient>
            </defs>
          </svg>

          <header className="c99-top">
            <span className="c99-logo" aria-label="Quizrr">
              <img src={isDarkTheme ? QuizrrLogoDark : QuizrrLogoLight} alt="Quizrr" />
            </span>
            <span className="c99-by">
              Powered by <b>MathonGo</b>
            </span>
          </header>

          <p className="c99-h c99-in">
            Stay consistent for the next 99 days with a clear plan, regular tests and <em>focused improvement.</em>
          </p>

          <section className="c99-jb c99-in d1" aria-labelledby="c99JbT">
            <span className="c99-pill c99-in d2">
              <span className="c99-cr">
                <CrownSimpleIcon className="c99-ic" weight="fill" />
              </span>
              <span>
                <span className="lg">Introducing </span>99 Percentile Challenge
              </span>
            </span>
            <h1 className="c99-jb-h c99-in d2" id="c99JbT">
              <span className="a">99 Days</span>
              <span className="g">99 Percentile</span>
            </h1>
            <button className="c99-cta c99-in d3" onClick={handleJoin} disabled={joining} type="button">
              {joining ? 'Joining…' : 'Join the Challenge'}
              <ArrowRightIcon className="c99-ic" weight="bold" />
            </button>
            {joinError && <p className="c99-join-error">{joinError}</p>}
          </section>

          <div className="c99-p c99-in d4">
            <StarIcon className="c99-star" weight="fill" />
            <span>
              <b className={bump ? 'c99-bump' : ''}>
                {joinedCount !== null ? joinedCount.toLocaleString('en-IN') : '—'}
              </b>{' '}
              students have already joined
            </span>
            <StarIcon className="c99-star" weight="fill" />
          </div>

          {tickerEntry && (
            <div className="c99-tk c99-in d5" aria-live="off">
              <div key={tickerKey} className="it">
                <img src={tickerEntry.photoUrl} alt="" />
                <span>
                  <b>{tickerEntry.name}</b> from {tickerEntry.city} has joined
                </span>
              </div>
            </div>
          )}

          <section className="c99-why" aria-labelledby="c99WhyT">
            <h2 className="c99-why-h" id="c99WhyT">
              Why join the <em>99 Percentile Challenge?</em>
            </h2>
            {WHY_ROWS.map((row, index) => (
              <WhyRow key={row.title} row={row} index={index} />
            ))}
          </section>

          <section className="c99-close" aria-labelledby="c99CtaT">
            <div className="c99-close-tr" aria-hidden="true">
              <TrophyIllustration />
            </div>
            <div>
              <h2 className="c99-close-t" id="c99CtaT">
                Compete with<em>the country&rsquo;s best.</em>
              </h2>
              <p className="c99-close-s">Challenge India&rsquo;s upcoming 99 percentilers in this 99-day challenge.</p>
            </div>
            <button className="c99-cta" onClick={handleJoin} disabled={joining} type="button">
              {joining ? 'Joining…' : 'Join the Challenge'}
              <ArrowRightIcon className="c99-ic" weight="bold" />
            </button>
          </section>
        </div>
      )}
    </AppLayout>
  );
}
